import { Component, Input, OnInit, OnChanges, SimpleChanges, Output, EventEmitter, ChangeDetectorRef } from '@angular/core';
import { DatePipe } from '@angular/common';
import { forkJoin, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { MatSnackBar } from '@angular/material/snack-bar';
import { DealsPipeLineService } from 'src/app/services/deals-pipe-line.service';
import { AfterSalesSubStatusService } from 'src/app/services/after-sales-sub-status.service';
import { DealsEntityService } from 'src/app/services/deals-entity.service';
import { DealsStageService } from 'src/app/services/deals-stage.service';
import { DownloadImageService } from 'src/app/services/download-image.service';
import { MatDialog } from '@angular/material/dialog';
import { SubStatusUpdateDialogComponent } from './sub-status-update-dialog.component';
import { environment } from 'src/environments/environment';

@Component({
  selector: 'app-after-sales-pipeline-tracker',
  templateUrl: './after-sales-pipeline-tracker.component.html',
  styleUrls: ['./after-sales-pipeline-tracker.component.scss']
})
export class AfterSalesPipelineTrackerComponent implements OnInit, OnChanges {

  /** Pass the full deal/lead object */
  @Input() entityObject: any;

  /** 'deal' | 'lead' — determines which API to call on sub-status update */
  @Input() entityType: 'deal' | 'lead' = 'deal';

  @Input() dealStages: any[] = [];

  /** Emits after a successful sub-status update so parent can reload */
  @Output() statusUpdated = new EventEmitter<void>();

  @Output() advanceDealStage = new EventEmitter<any>();

  stageList: any[] = [];
  subStatusList: any[] = [];
  private _historyList: any[] = [];
  get historyList(): any[] {
    return this._historyList;
  }
  set historyList(val: any[]) {
    this._historyList = val || [];
    this.refreshHistoryDerivedData();
  }
  showHistory: boolean = false;
  isUpdating: boolean = false;
  isLoading: boolean = false;

  currentPipelineId: number | null = null;
  currentPipelineName: string = '';
  currentStageIndex: number = -1;
  currentSubStatusIndex: number = -1;
  userSelectedStageIndex: number | null = null;

  get hasPipeline(): boolean {
    return !!(this.currentPipelineId && this.stageList && this.stageList.length > 0);
  }

  get currentStage(): any {
    return this.stageList[this.currentStageIndex] || null;
  }

  get isLastStage(): boolean {
    return this.stageList && this.stageList.length > 0 && this.currentStageIndex === this.stageList.length - 1;
  }

  private parseTimestamp(dateVal: any): number {
    if (!dateVal) return 0;
    if (typeof dateVal === 'number') return dateVal;
    const t = new Date(dateVal).getTime();
    if (!isNaN(t)) return t;
    const normalized = String(dateVal).replace(/\.\d+/, '').replace(/\+.*$/, 'Z');
    const t2 = new Date(normalized).getTime();
    return isNaN(t2) ? 0 : t2;
  }

  get isLastStageCompleted(): boolean {
    if (!this.isLastStage) {
      return false;
    }
    if (this.entityObject?.afterSalesCompletedDate) {
      return true;
    }
    return false;
  }

  isSubStatusCompletedByIndex(index: number): boolean {
    if (!this.subStatusList || !this.subStatusList[index]) return false;
    if (this.entityObject?.afterSalesCompletedDate) return true;
    const sub = this.subStatusList[index];
    const subId = Number(sub.statusId || sub.id || 0);
    const subName = (sub.statusName || '').trim().toLowerCase();

    // Current stage's ID — used to filter out records belonging to other stages.
    const currentStageObj = this.currentStage;
    const currentStageId = currentStageObj
      ? Number(currentStageObj.pipelineStageId || currentStageObj.id || currentStageObj.statusId || 0)
      : 0;

    // afterSalesStartedDate — only count records from the CURRENT session at this stage.
    const stageStartedDate = this.entityObject?.afterSalesStartedDate
      ? this.parseTimestamp(this.entityObject.afterSalesStartedDate)
      : 0;

    return (this.historyList || []).some((h: any) => {
      if (currentStageId > 0 && Number(h.stageId) !== currentStageId) return false;

      if (stageStartedDate > 0 && h.changedAt) {
        const histTime = this.parseTimestamp(h.changedAt);
        if (histTime > 0 && histTime < stageStartedDate - 60000) return false;
      }

      const matchesSub = Number(h.toSubStatusId) === subId ||
        (h.toSubStatusName && sub.statusName && h.toSubStatusName.trim().toLowerCase() === subName);
      if (!matchesSub) return false;

      if (h.notes && h.notes.toLowerCase().includes('auto-advanced to next pipeline stage')) return false;

      const isSelfUpdate = Number(h.fromSubStatusId) === subId;
      const fromInSameStage = this.subStatusList.some(
        (s: any) => Number(s.statusId || s.id) === Number(h.fromSubStatusId)
      );
      const hasContent = (h.notes && h.notes.trim().length > 0) ||
        (h.images && h.images !== '[]' && h.images.trim().length > 0) ||
        (h.formValues && h.formValues !== '{}' && h.formValues.trim().length > 0);

      return isSelfUpdate || (fromInSameStage && (hasContent || !!h.changedByName));
    });
  }

  get isCurrentStageCompleted(): boolean {
    if (this.isLastStageCompleted) {
      return true;
    }
    if (this.isLastStage) {
      if (this.subStatusList && this.subStatusList.length > 0) {
        return this.isSubStatusCompletedByIndex(this.subStatusList.length - 1);
      }
      return false;
    }
    if (!this.subStatusList || this.subStatusList.length === 0) {
      return true;
    }
    const lastIndex = this.subStatusList.length - 1;
    return this.isSubStatusCompletedByIndex(lastIndex);
  }

  get isReadyToAdvance(): boolean {
    if (this.currentStageIndex === -1 || this.currentStageIndex >= this.stageList.length - 1) {
      return false;
    }
    if (!this.subStatusList || this.subStatusList.length === 0) {
      return true;
    }
    // All sub-statuses must be truly completed (not just at last index position)
    return this.isCurrentStageCompleted;
  }

  get completedSubStatusCount(): number {
    if (!this.subStatusList || this.subStatusList.length === 0) return 0;
    if (this.isLastStageCompleted) return Math.max(0, this.subStatusList.length - 1);
    let count = 0;
    for (let i = 0; i < this.subStatusList.length; i++) {
      if (this.isSubStatusCompletedByIndex(i)) {
        count++;
      } else {
        break;
      }
    }
    return Math.min(count, Math.max(0, this.subStatusList.length - 1));
  }

  isSubStatusClickable(index: number): boolean {
    if (this.isLastStageCompleted || this.isCurrentStageCompleted) return false;
    if (this.isSubStatusCompletedByIndex(index)) return false;
    for (let k = 0; k < index; k++) {
      if (!this.isSubStatusCompletedByIndex(k)) {
        return false;
      }
    }
    return true;
  }

  constructor(
    private dealsPipeLineService: DealsPipeLineService,
    private afterSalesSubStatusService: AfterSalesSubStatusService,
    private dealsEntityService: DealsEntityService,
    private dealsStageService: DealsStageService,
    private downloadImageService: DownloadImageService,
    private snackBar: MatSnackBar,
    private cdr: ChangeDetectorRef,
    public dialog: MatDialog
  ) { }

  ngOnInit(): void {
    this.loadData();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['entityObject'] || changes['dealStages']) {
      if (changes['entityObject'] && changes['entityObject'].previousValue && changes['entityObject'].currentValue) {
        const prev = changes['entityObject'].previousValue;
        const curr = changes['entityObject'].currentValue;
        if (prev.entityId === curr.entityId) {
          if (!curr.afterSalesStageId && prev.afterSalesStageId) {
            curr.afterSalesStageId = prev.afterSalesStageId;
          }
          if (!curr.afterSalesStatus && prev.afterSalesStatus) {
            curr.afterSalesStatus = prev.afterSalesStatus;
          }
          if (!curr.afterSalesSubStatusId && prev.afterSalesSubStatusId) {
            curr.afterSalesSubStatusId = prev.afterSalesSubStatusId;
          }
        }
      }
      this.loadData();
    }
  }

  private getLatestHistoryRecord(historyList: any[]): any {
    if (!historyList || historyList.length === 0) return null;

    let best = historyList[0];

    for (let i = 1; i < historyList.length; i++) {
      const current = historyList[i];

      const bestTime = this.parseTimestamp(best.changedAt || best.createdAt);
      const currentTime = this.parseTimestamp(current.changedAt || current.createdAt);

      const bestId = Number(best.id || best.historyId || best.afterSalesHistoryId || 0);
      const currentId = Number(current.id || current.historyId || current.afterSalesHistoryId || 0);

      if (currentTime > bestTime) {
        best = current;
      } else if (currentTime < bestTime) {
        continue;
      } else {
        if (currentId > bestId) {
          best = current;
        }
      }
    }
    return best;
  }

  private lastLoadedEntityId: any = null;
  private lastLoadedStatusId: any = null;
  private lastLoadedStatusName: any = null;
  private lastLoadedAfterSalesStageId: any = null;
  private lastLoadedAfterSalesSubStatusId: any = null;
  private lastLoadedAfterSalesStatus: any = null;

  loadData(forceReload: boolean = false): void {
    if (!this.entityObject?.entityId) {
      this.clearPipeline();
      this.lastLoadedEntityId = null;
      return;
    }

    const currentStatusId = this.entityObject.statusId ?? this.entityObject.leadStatus ?? this.entityObject.Stage;
    const currentStatusName = this.entityObject.statusName || this.entityObject.dealStage || this.entityObject.stage;
    const currentAfterSalesStageId = this.entityObject.afterSalesStageId;
    const currentAfterSalesSubStatusId = this.entityObject.afterSalesSubStatusId;
    const currentAfterSalesStatus = this.entityObject.afterSalesStatus;

    if (!forceReload &&
        this.lastLoadedEntityId === this.entityObject.entityId &&
        this.lastLoadedStatusId === currentStatusId &&
        this.lastLoadedStatusName === currentStatusName &&
        this.lastLoadedAfterSalesStageId === currentAfterSalesStageId &&
        this.lastLoadedAfterSalesSubStatusId === currentAfterSalesSubStatusId &&
        this.lastLoadedAfterSalesStatus === currentAfterSalesStatus &&
        this.dealStages && this.dealStages.length > 0 &&
        this.currentPipelineId !== null) {
      return;
    }

    if (this.lastLoadedEntityId !== this.entityObject.entityId) {
      this.userSelectedStageIndex = null;
    }

    this.lastLoadedEntityId = this.entityObject.entityId;
    this.lastLoadedStatusId = currentStatusId;
    this.lastLoadedStatusName = currentStatusName;
    this.lastLoadedAfterSalesStageId = currentAfterSalesStageId;
    this.lastLoadedAfterSalesSubStatusId = currentAfterSalesSubStatusId;
    this.lastLoadedAfterSalesStatus = currentAfterSalesStatus;

    if (this.dealStages && this.dealStages.length > 0) {
      this.processStagePipeline(this.dealStages, currentStatusId, currentStatusName);
    } else {
      const typeId = this.entityType === 'lead' ? 2 : 4;
      this.dealsStageService.getAllDealsStagesDropDown(typeId).subscribe({
        next: (stages: any) => {
          const stageList = Array.isArray(stages) ? stages : (stages?.data || stages?.content || []);
          this.dealStages = stageList;
          this.processStagePipeline(stageList, currentStatusId, currentStatusName);
        },
        error: () => {
          this.clearPipeline();
        }
      });
    }
  }

  private processStagePipeline(stages: any[], statusId: any, statusName: string): void {
    const matchedStage = (stages || []).find((s: any) => {
      const idMatches = (statusId !== undefined && statusId !== null && statusId !== '')
        ? (String(s.statusId) === String(statusId) || String(s.id) === String(statusId))
        : false;

      const nameMatches = (statusName && s.statusName)
        ? (s.statusName.trim().toLowerCase() === statusName.trim().toLowerCase())
        : false;

      if (idMatches && nameMatches) return true;
      if (idMatches && !statusName) return true;
      if (nameMatches && (statusId === undefined || statusId === null || statusId === '')) return true;

      return false;
    });

    const finalMatchedStage = matchedStage || (stages || []).find((s: any) => {
      if (statusName && s.statusName && s.statusName.trim().toLowerCase() === statusName.trim().toLowerCase()) return true;
      if (statusId !== undefined && statusId !== null && statusId !== '' && (String(s.statusId) === String(statusId) || String(s.id) === String(statusId))) return true;
      return false;
    });

    if (!finalMatchedStage) {
      this.clearPipeline();
      return;
    }

    let rawPipelineIds: any = finalMatchedStage?.pipelineIds ?? finalMatchedStage?.pipelineId ?? finalMatchedStage?.associatedPipelineId ?? finalMatchedStage?.pipeLineId ?? finalMatchedStage?.pipeLineIds;
    let pipelineIds: number[] = [];
    if (Array.isArray(rawPipelineIds)) {
      pipelineIds = rawPipelineIds.map(id => Number(id)).filter(id => !isNaN(id) && id > 0);
    } else if (rawPipelineIds !== undefined && rawPipelineIds !== null && Number(rawPipelineIds) > 0) {
      pipelineIds = [Number(rawPipelineIds)];
    }

    // Stage has no after-sales pipeline linked
    if (!pipelineIds || pipelineIds.length === 0) {
      this.clearPipeline();
      return;
    }

    const targetPipelineId = Number(pipelineIds[0]);
    this.loadPipelineDetails(targetPipelineId);
  }

  private loadPipelineDetails(pipelineId: number): void {
    this.isLoading = true;

    forkJoin({
      pipe: this.dealsPipeLineService.getDealsPipeLineByIdAndActive(pipelineId).pipe(catchError(() => of(null))),
      historyRes: this.dealsEntityService.getAfterSalesHistory(this.entityObject.entityId).pipe(catchError(() => of(null))),
      stagesRes: this.dealsPipeLineService.getDealsPipeLineStagesById(pipelineId).pipe(catchError(() => of([])))
    }).subscribe({
      next: ({ pipe, historyRes, stagesRes }) => {
        if (!pipe || (pipe.active !== undefined && Number(pipe.active) === 0)) {
          this.clearPipeline();
          return;
        }

        this.currentPipelineId = pipelineId;
        this.currentPipelineName = pipe.pipelineName || pipe.name || '';

        const allHistory = historyRes?.datalist || historyRes || [];
        this.historyList = allHistory;

        let pipelineHistory = (allHistory || []).filter((h: any) =>
          !h.pipelineId || Number(h.pipelineId) === Number(pipelineId)
        );
        if (pipelineHistory.length === 0 && allHistory.length > 0) {
          pipelineHistory = allHistory;
        }

        const latestHistory = this.getLatestHistoryRecord(pipelineHistory);

        const rawStages = Array.isArray(stagesRes) ? stagesRes : [];
        this.stageList = rawStages.sort((a: any, b: any) => (a.position || 0) - (b.position || 0));

        if (this.stageList.length === 0) {
          this.clearPipeline();
          return;
        }

        let matchedStage: any = null;
        let activeSubStatusId: number | null = null;

        // 0. Try match by userSelectedStageIndex FIRST (preserve user's active stage session)
        if (this.userSelectedStageIndex !== null && this.userSelectedStageIndex >= 0 && this.userSelectedStageIndex < this.stageList.length) {
          matchedStage = this.stageList[this.userSelectedStageIndex];
        }

        // 1-4. On fresh page load, select the highest candidate stage index among server state & history
        if (!matchedStage) {
          const candidateIndices: number[] = [];

          // Candidate 1: entityObject.afterSalesStageId
          if (this.entityObject?.afterSalesStageId) {
            const targetId = Number(this.entityObject.afterSalesStageId);
            const idx = this.stageList.findIndex((s: any) =>
              Number(s.pipelineStageId) === targetId ||
              Number(s.statusId) === targetId ||
              Number(s.id) === targetId ||
              Number(s.stageId) === targetId
            );
            if (idx >= 0) candidateIndices.push(idx);
          }

          // Candidate 2: entityObject.afterSalesStatus
          if (this.entityObject?.afterSalesStatus && String(this.entityObject.afterSalesStatus).toLowerCase() !== 'completed') {
            const targetName = String(this.entityObject.afterSalesStatus).trim().toLowerCase();
            const idx = this.stageList.findIndex((s: any) =>
              (s.statusName && String(s.statusName).trim().toLowerCase() === targetName) ||
              (s.stageName && String(s.stageName).trim().toLowerCase() === targetName) ||
              (s.name && String(s.name).trim().toLowerCase() === targetName)
            );
            if (idx >= 0) candidateIndices.push(idx);
          }

          // Candidate 3: latestHistory record stage
          if (latestHistory) {
            const histStageId = Number(
              latestHistory.stageId ||
              latestHistory.pipelineStageId ||
              latestHistory.afterSalesStageId ||
              latestHistory.statusId ||
              latestHistory.toStageId || 0
            );
            if (histStageId > 0) {
              const idx = this.stageList.findIndex((s: any) =>
                Number(s.pipelineStageId) === histStageId ||
                Number(s.statusId) === histStageId ||
                Number(s.id) === histStageId ||
                Number(s.stageId) === histStageId
              );
              if (idx >= 0) candidateIndices.push(idx);
            }
            const histStageName = String(
              latestHistory.stageName ||
              latestHistory.pipelineStageName ||
              latestHistory.toStageName ||
              latestHistory.stage ||
              latestHistory.statusName || ''
            ).trim().toLowerCase();
            if (histStageName) {
              const idx = this.stageList.findIndex((s: any) =>
                (s.statusName && String(s.statusName).trim().toLowerCase() === histStageName) ||
                (s.stageName && String(s.stageName).trim().toLowerCase() === histStageName) ||
                (s.name && String(s.name).trim().toLowerCase() === histStageName)
              );
              if (idx >= 0) candidateIndices.push(idx);
            }
          }

          // Candidate 4: Highest stage index with any history records
          if (allHistory && allHistory.length > 0) {
            for (let i = this.stageList.length - 1; i >= 0; i--) {
              const stg = this.stageList[i];
              const stgId = Number(stg.pipelineStageId || stg.statusId || stg.id || stg.stageId || 0);
              const stgName = String(stg.statusName || stg.stageName || stg.name || '').trim().toLowerCase();
              const hasHist = allHistory.some((h: any) => {
                const hStageId = Number(h.stageId || h.pipelineStageId || h.toStageId || 0);
                const hStageName = String(h.stageName || h.pipelineStageName || h.toStageName || '').trim().toLowerCase();
                if (stgId > 0 && hStageId > 0 && stgId === hStageId) return true;
                if (stgName && hStageName && stgName === hStageName) return true;
                return false;
              });
              if (hasHist) {
                candidateIndices.push(i);
                break;
              }
            }
          }

          if (candidateIndices.length > 0) {
            const maxIdx = Math.max(...candidateIndices);
            matchedStage = this.stageList[maxIdx];
          } else {
            matchedStage = this.stageList[0];
          }
        }

        const stageIndex = this.stageList.indexOf(matchedStage);
        this.currentStageIndex = stageIndex >= 0 ? stageIndex : 0;
        const activeStageId = matchedStage.pipelineStageId || matchedStage.id || matchedStage.statusId || matchedStage.stageId;

        // Keep entityObject fields updated so parent reloads or ngOnChanges retain current stage
        if (this.entityObject) {
          if (activeStageId) {
            this.entityObject.afterSalesStageId = activeStageId;
            this.lastLoadedAfterSalesStageId = activeStageId;
          }
          if (matchedStage.statusName) {
            this.entityObject.afterSalesStatus = matchedStage.statusName;
            this.lastLoadedAfterSalesStatus = matchedStage.statusName;
          }
        }

        if (latestHistory && (latestHistory.toSubStatusId || latestHistory.subStatusId)) {
          activeSubStatusId = Number(latestHistory.toSubStatusId || latestHistory.subStatusId);
        } else if (this.entityObject?.afterSalesSubStatusId) {
          activeSubStatusId = Number(this.entityObject.afterSalesSubStatusId);
        }

        // Store only the pipeline ID on the entity — do NOT overwrite afterSalesStageId or
        // afterSalesStatus. Those fields are owned by the parent (server-side values) and are used
        // by processStagePipeline for deal-stage → pipeline matching. Overwriting them causes the
        // next ngOnChanges to mismatch the deal stage → clearPipeline() fires → "pipeline goes missing".
        this.entityObject.afterSalesPipelineId = pipelineId;

        if (activeStageId) {
          this.afterSalesSubStatusService.getSubStatusesByPipelineStageId(activeStageId).subscribe({
            next: (subs: any) => {
              const rawSubs = Array.isArray(subs) ? subs : (subs?.data || subs?.content || []);
              this.subStatusList = rawSubs.slice().sort((a: any, b: any) => (a.sortOrder || 0) - (b.sortOrder || 0));

              if (this.subStatusList.length > 0) {
                // Find first pending (not completed) sub-status
                let firstPendingIndex = -1;
                for (let i = 0; i < this.subStatusList.length; i++) {
                  if (!this.isSubStatusCompletedByIndex(i)) {
                    firstPendingIndex = i;
                    break;
                  }
                }
                this.currentSubStatusIndex = firstPendingIndex >= 0 ? firstPendingIndex : this.subStatusList.length - 1;
              } else {
                this.currentSubStatusIndex = -1;
              }
              // Do NOT overwrite entityObject.afterSalesSubStatusId here.
              // It must only be set by performUpdate when the user actually submits.

              this.isLoading = false;
              this.cdr.detectChanges();
            },
            error: () => {
              this.subStatusList = [];
              this.currentSubStatusIndex = -1;
              this.isLoading = false;
              this.cdr.detectChanges();
            }
          });
        } else {
          this.subStatusList = [];
          this.currentSubStatusIndex = -1;
          this.isLoading = false;
          this.cdr.detectChanges();
        }
      },
      error: () => {
        this.clearPipeline();
        this.cdr.detectChanges();
      }
    });
  }

  private clearPipeline(): void {
    this.currentPipelineId = null;
    this.currentPipelineName = '';
    this.stageList = [];
    this.subStatusList = [];
    this.historyList = [];
    this.currentStageIndex = -1;
    this.currentSubStatusIndex = -1;
    this.userSelectedStageIndex = null;
    this.lastLoadedEntityId = null;
    this.lastLoadedStatusId = null;
    this.lastLoadedStatusName = null;
    this.isLoading = false;
  }

  openCurrentSubStatusDialog(): void {
    if (this.isLastStageCompleted || this.isCurrentStageCompleted) return;
    // currentSubStatusIndex is the NEXT pending sub-status (not the last done).
    // Open it directly – no +1 jump needed.
    if (this.currentSubStatusIndex >= 0 && this.subStatusList[this.currentSubStatusIndex]) {
      this.openSubStatusDialog(this.subStatusList[this.currentSubStatusIndex], this.currentSubStatusIndex);
    } else if (this.subStatusList.length > 0) {
      this.openSubStatusDialog(this.subStatusList[0], 0);
    }
  }

  selectSubStatus(sub: any, index: number): void {
    if (this.isLastStageCompleted || this.isCurrentStageCompleted) return;
    // Prevent re-opening already-completed sub-statuses (green checkmark steps)
    if (this.isSubStatusCompletedByIndex(index)) return;

    // Check if any previous sub-status is NOT completed
    for (let k = 0; k < index; k++) {
      if (!this.isSubStatusCompletedByIndex(k)) {
        const firstIncompleteSub = this.subStatusList[k];
        const stepName = firstIncompleteSub?.statusName || 'the first step';
        this.snackBar.open(
          `Please complete step "${stepName}" first before moving to the next stage.`,
          '×',
          { panelClass: 'warning', duration: 4000 }
        );
        return;
      }
    }

    this.openSubStatusDialog(sub, index);
  }

  onStageChevronClick(stage: any, index: number): void {
    if (index === this.currentStageIndex) return;

    if (index > this.currentStageIndex) {
      if (this.subStatusList && this.subStatusList.length > 0 && !this.isCurrentStageCompleted) {
        const activeSub = this.subStatusList[this.currentSubStatusIndex >= 0 ? this.currentSubStatusIndex : 0];
        const stepName = activeSub?.statusName || this.currentStage?.statusName || 'the current step';
        this.snackBar.open(
          `Please complete step "${stepName}" first before moving to the next stage.`,
          '×',
          { panelClass: 'warning', duration: 4000 }
        );
        return;
      }
    }

    this.userSelectedStageIndex = index;
    this.currentStageIndex = index;
    const targetStageId = stage.pipelineStageId || stage.id || stage.statusId || stage.stageId;
    if (targetStageId) {
      this.afterSalesSubStatusService.getSubStatusesByPipelineStageId(targetStageId).subscribe({
        next: (subs: any) => {
          const rawSubs = Array.isArray(subs) ? subs : (subs?.data || subs?.content || []);
          this.subStatusList = rawSubs.slice().sort((a: any, b: any) => (a.sortOrder || 0) - (b.sortOrder || 0));
          if (this.subStatusList.length > 0) {
            let firstPendingIndex = -1;
            for (let i = 0; i < this.subStatusList.length; i++) {
              if (!this.isSubStatusCompletedByIndex(i)) {
                firstPendingIndex = i;
                break;
              }
            }
            this.currentSubStatusIndex = firstPendingIndex >= 0 ? firstPendingIndex : this.subStatusList.length - 1;
          } else {
            this.currentSubStatusIndex = -1;
          }
          this.cdr.detectChanges();
        },
        error: () => {
          this.subStatusList = [];
          this.currentSubStatusIndex = -1;
          this.cdr.detectChanges();
        }
      });
    }
  }

  openSubStatusDialog(sub: any, index: number): void {
    if (this.isUpdating) return;
    if (!this.entityObject?.entityId) return;

    const isFinalStage = index === this.subStatusList.length - 1;
    const requireNotes = sub.requireNotes === 1;
    const requireImages = sub.requireImages === 1;
    const hasDynamicForm = sub.dynamicFormGroupId && Number(sub.dynamicFormGroupId) > 0;

    const dialogRef = this.dialog.open(SubStatusUpdateDialogComponent, {
      width: hasDynamicForm ? '700px' : '550px',
      data: {
        dialogTitle: `Update Sub-Status: ${sub.statusName}`,
        nodeName: sub.statusName,
        buttonText: 'Update & Save',
        isFinalStage: isFinalStage,
        requireNotes: requireNotes,
        requireImages: requireImages,
        dynamicFormGroupId: sub.dynamicFormGroupId,
        entityObject: this.entityObject,
        entityType: this.entityType,
        dealStages: this.dealStages,
        currentDealStage: this.entityObject?.statusName || this.entityObject?.dealStage || this.entityObject?.stage,
        saveCallback: (payload: any) => this.performUpdate(sub, index, payload)
      }
    });

    dialogRef.afterClosed().subscribe(() => {
    });
  }

  private performUpdate(sub: any, index: number, payload: any): Promise<void> {
    return new Promise((resolve, reject) => {
      this.isUpdating = true;
      const currentStageObj = this.currentStage;
      const currentStageId = currentStageObj
        ? (currentStageObj.pipelineStageId || currentStageObj.id || currentStageObj.statusId || currentStageObj.stageId)
        : (sub.pipelineStageId || sub.associatedPipelineId || sub.stageId || this.entityObject?.afterSalesStageId);

      const enrichedPayload = {
        ...payload,
        pipelineId: String(this.currentPipelineId || this.entityObject?.afterSalesPipelineId || ''),
        stageId: String(currentStageId || '')
      };
      this.dealsEntityService.updateAfterSalesSubStatusWithImages(this.entityObject.entityId, sub.statusId, enrichedPayload).subscribe({
        next: () => {
          this.snackBar.open('After-Sales status updated!', '×', { panelClass: 'success', duration: 3000 });
          this.entityObject.afterSalesSubStatusId = sub.statusId;
          if (currentStageId) {
            this.entityObject.afterSalesStageId = currentStageId;
          }
          if (currentStageObj?.statusName) {
            this.entityObject.afterSalesStatus = currentStageObj.statusName;
          }
          this.currentSubStatusIndex = index;
          if (this.currentStageIndex >= 0) {
            this.userSelectedStageIndex = this.currentStageIndex;
          }

          const isLastStage = this.currentStageIndex === this.stageList.length - 1;
          const isLastSub = index === this.subStatusList.length - 1 || this.subStatusList.length === 0;
          if (isLastStage && isLastSub) {
            this.entityObject.afterSalesCompletedDate = new Date().toISOString();
          }

          // Statically persist dynamic form field values to deal entity
          if (payload?.dynamicFormValues && Object.keys(payload.dynamicFormValues).length > 0) {
            this.entityObject.customField10 = {
              ...(this.entityObject.customField10 || {}),
              ...payload.dynamicFormValues
            };
            Object.assign(this.entityObject, payload.dynamicFormValues);

            const dealPayload = {
              ...this.entityObject,
              ...payload.dynamicFormValues,
              customField10: this.entityObject.customField10
            };
            this.dealsEntityService.editDeals(dealPayload, this.entityObject.entityId).subscribe({
              next: () => { },
              error: (err) => console.warn('Deal entity static fields save returned:', err)
            });
          }

          this.isUpdating = false;
          // Reset dedup keys so the next ngOnChanges (from parent reload) always runs loadData.
          this.lastLoadedEntityId = null;
          this.lastLoadedAfterSalesStageId = null;
          this.lastLoadedAfterSalesSubStatusId = null;
          this.lastLoadedAfterSalesStatus = null;

          if (this.showHistory) {
            this.loadHistory();
          }

          this.cdr.detectChanges();

          // Let the parent confirm the server state (entity reload via ngOnChanges).
          this.statusUpdated.emit();
          resolve();
        },
        error: (err) => {
          this.snackBar.open(err?.error?.status_message || err?.error || 'Failed to update status', '×', { panelClass: 'error', duration: 4000 });
          this.isUpdating = false;
          this.cdr.detectChanges();
          reject(err);
        }
      });
    });
  }

  toggleHistory(): void {
    this.showHistory = !this.showHistory;
    if (this.showHistory && this.historyList.length === 0) {
      this.loadHistory();
    }
  }

  loadHistory(): void {
    const dealId = this.entityObject?.entityId || this.entityObject?.id;
    if (!dealId) return;
    this.dealsEntityService.getAfterSalesHistory(dealId).subscribe((res: any) => {
      this.historyList = res?.datalist || res || [];
      this.cdr.detectChanges();
    });
  }

  getSubStatusClass(index: number): string {
    if (index < this.currentSubStatusIndex || (index === this.currentSubStatusIndex && this.isLastStageCompleted)) {
      return 'step-done';
    }
    if (index === this.currentSubStatusIndex) return 'step-active';
    return 'step-pending';
  }

  getStageClass(index: number): string {
    if (index < this.currentStageIndex || (index === this.currentStageIndex && this.isLastStageCompleted)) {
      return 'stage-done';
    }
    if (index === this.currentStageIndex) return 'stage-active';
    return 'stage-pending';
  }

  /** Called when current stage has no sub-statuses or ready to advance */
  advanceToNextStageManually(): void {
    if (this.isUpdating) return;
    const dealId = this.entityObject?.entityId || this.entityObject?.id;
    if (!dealId) return;

    // Validate if the current stage has sub-tasks that are not completed
    if (this.subStatusList && this.subStatusList.length > 0 && !this.isCurrentStageCompleted) {
      const activeIndex = this.currentSubStatusIndex >= 0 ? this.currentSubStatusIndex : 0;
      const currentSub = this.subStatusList[activeIndex];
      const stepName = currentSub?.statusName || 'current step';
      this.snackBar.open(`Please complete step "${stepName}" first before moving to the next stage.`, '×', { panelClass: 'warning', duration: 4000 });
      return;
    }

    const nextStageIndex = this.currentStageIndex + 1;
    if (nextStageIndex >= this.stageList.length) return;

    const nextStage = this.stageList[nextStageIndex];
    const targetStageId = nextStage?.pipelineStageId || nextStage?.id || nextStage?.statusId;
    if (!targetStageId) return;

    this.isUpdating = true;
    this.afterSalesSubStatusService.getSubStatusesByPipelineStageId(targetStageId).subscribe({
      next: (subs: any) => {
        const rawSubs = Array.isArray(subs) ? subs : (subs?.data || subs?.content || []);
        const sorted = rawSubs.slice().sort((a: any, b: any) => (a.sortOrder || 0) - (b.sortOrder || 0));
        const firstSub = sorted.length > 0 ? sorted[0] : null;
        const payload: any = {};
        if (firstSub && (firstSub.statusId || firstSub.id)) {
          payload.subStatusId = firstSub.statusId || firstSub.id;
        }
        this.performStageAdvance(nextStage, nextStageIndex, payload);
      },
      error: () => {
        this.performStageAdvance(nextStage, nextStageIndex, {});
      }
    });
  }

  completePipeline(): void {
    if (this.isUpdating) return;
    const dealId = this.entityObject?.entityId || this.entityObject?.id;
    if (!dealId) return;

    this.isUpdating = true;
    const completedDate = new Date().toISOString();
    this.entityObject.afterSalesCompletedDate = completedDate;
    this.entityObject.afterSalesStatus = 'Completed';

    const dealPayload = {
      ...this.entityObject,
      afterSalesCompletedDate: completedDate,
      afterSalesStatus: 'Completed'
    };

    this.dealsEntityService.editDeals(dealPayload, dealId).subscribe({
      next: () => {
        this.isUpdating = false;
        this.snackBar.open('After-Sales Pipeline Completed!', '×', { panelClass: 'success', duration: 3000 });
        this.statusUpdated.emit();
        this.cdr.detectChanges();
      },
      error: (err) => {
        this.isUpdating = false;
        this.snackBar.open(err?.error?.status_message || 'Failed to complete pipeline', '×', { panelClass: 'error', duration: 4000 });
        this.cdr.detectChanges();
      }
    });
  }

  private performStageAdvance(nextStage: any, nextStageIndex: number, payload: any): Promise<void> {
    return new Promise((resolve, reject) => {
      this.isUpdating = true;
      const targetStageId = nextStage?.pipelineStageId || nextStage?.id || nextStage?.statusId;
      const enrichedPayload = {
        ...payload,
        pipelineId: String(this.currentPipelineId || this.entityObject?.afterSalesPipelineId || ''),
        stageId: String(targetStageId || '')
      };

      const dealId = this.entityObject?.entityId || this.entityObject?.id;

      this.dealsEntityService.advanceAfterSalesStage(dealId, targetStageId, enrichedPayload).subscribe({
        next: () => {
          this.isUpdating = false;
          this.snackBar.open(`Moved to ${nextStage.statusName}!`, '×', { panelClass: 'success', duration: 3000 });
          this.entityObject.afterSalesStageId = targetStageId;
          this.entityObject.afterSalesStatus = nextStage.statusName;
          this.entityObject.afterSalesSubStatusId = payload?.subStatusId || null;
          this.currentStageIndex = nextStageIndex;
          this.userSelectedStageIndex = nextStageIndex;
          this.lastLoadedEntityId = null;

          if (this.showHistory) {
            this.loadHistory();
          }
          if (this.currentPipelineId || enrichedPayload.pipelineId) {
            this.loadPipelineDetails(Number(this.currentPipelineId || enrichedPayload.pipelineId));
          }
          this.statusUpdated.emit();
          this.cdr.detectChanges();
          resolve();
        },
        error: (err) => {
          this.isUpdating = false;
          this.snackBar.open(err?.error?.status_message || err?.error || 'Failed to advance stage', '×', { panelClass: 'error', duration: 4000 });
          this.cdr.detectChanges();
          reject(err);
        }
      });
    });
  }

  public serverDocPath = environment.commonImageApi + 'stageHistory/';
  public historyViewMode: 'timeline' | 'documents' | 'form-data' = 'timeline';

  getFileUrl(fileName: string): string {
    if (!fileName) return '';
    if (fileName.startsWith('http://') || fileName.startsWith('https://')) {
      return fileName;
    }
    return this.serverDocPath + fileName;
  }

  parseHistoryDocuments(imagesStr: any): string[] {
    if (!imagesStr) return [];
    if (Array.isArray(imagesStr)) return imagesStr;
    try {
      const parsed = JSON.parse(imagesStr);
      if (Array.isArray(parsed)) return parsed;
      return [imagesStr];
    } catch (e) {
      return String(imagesStr).includes(',') ? String(imagesStr).split(',').map(s => s.trim()) : [String(imagesStr)];
    }
  }

  parseFormValues(formValuesStr: any): Array<{ key: string; label: string; value: any }> {
    if (!formValuesStr) return [];
    try {
      const obj = typeof formValuesStr === 'string' ? JSON.parse(formValuesStr) : formValuesStr;
      if (!obj || typeof obj !== 'object' || Array.isArray(obj)) return [];
      return Object.keys(obj).map(key => ({
        key,
        label: this.formatFieldLabel(key),
        value: this.formatFieldValue(obj[key])
      })).filter(item => item.value !== null && item.value !== undefined && item.value !== '');
    } catch (e) {
      return [];
    }
  }

  formatFieldLabel(key: string): string {
    if (!key) return '';
    const result = key.replace(/([A-Z])/g, ' $1').replace(/[_-]/g, ' ');
    return result.charAt(0).toUpperCase() + result.slice(1).trim();
  }

  formatFieldValue(val: any): string {
    if (val === null || val === undefined) return '';
    if (typeof val === 'boolean') return val ? 'Yes' : 'No';
    if (typeof val === 'object') {
      if (val instanceof Date) {
        return new DatePipe('en-US').transform(val, 'dd MMM yyyy') || String(val);
      }
      try {
        return JSON.stringify(val);
      } catch (e) {
        return String(val);
      }
    }
    const strVal = String(val).trim();
    if (/^\d{4}-\d{2}-\d{2}(T\d{2}:\d{2}:\d{2}(\.\d{3})?([+-]\d{2}:?\d{2}|Z)?)?$/.test(strVal)) {
      const parsedDate = new Date(strVal);
      if (!isNaN(parsedDate.getTime())) {
        return new DatePipe('en-US').transform(parsedDate, 'dd MMM yyyy') || strVal;
      }
    }
    return strVal;
  }

  private _allHistoryDocuments: Array<{
    fileName: string;
    stageName: string;
    subStatusName: string;
    uploadedBy: string;
    uploadedAt: any;
    fileUrl: string;
    isImage: boolean;
    isPdf: boolean;
    isWord: boolean;
    isExcel: boolean;
    isVideo: boolean;
    isAudio: boolean;
    fileTypeLabel: string;
  }> = [];

  private _allHistoryFormEntries: Array<{
    stageName: string;
    subStatusName: string;
    submittedBy: string;
    submittedAt: any;
    fields: Array<{ key: string; label: string; value: any }>;
    // }> {
    //   const entries: any[] = [];
    //   if (!this.historyList || this.historyList.length === 0) return entries;

    //   this.historyList.forEach(h => {
    //     if (h.formValues) {
    //       const fields = this.parseFormValues(h.formValues);
    //       if (fields.length > 0) {
    //         entries.push({
    //           stageName: h.stageName || '',
    //           subStatusName: h.toSubStatusName || '',
    //           submittedBy: h.changedByName || '',
    //           submittedAt: h.changedAt,
    //           fields: fields
    //         });
    //       }
    //     }
    //   });
    //   return entries;
    // }
  }> = [];
  get allHistoryDocuments(): Array<{
    fileName: string;
    stageName: string;
    subStatusName: string;
    uploadedBy: string;
    uploadedAt: any;
    fileUrl: string;
    isImage: boolean;
    isPdf: boolean;
    isWord: boolean;
    isExcel: boolean;
    isVideo: boolean;
    isAudio: boolean;
    fileTypeLabel: string;
  }> {
    return this._allHistoryDocuments;
  }

  get allHistoryFormEntries(): Array<{
    stageName: string;
    subStatusName: string;
    submittedBy: string;
    submittedAt: any;
    fields: Array<{ key: string; label: string; value: any }>;
  }> {
    return this._allHistoryFormEntries;
  }

  private refreshHistoryDerivedData(): void {
    // 1. Documents
    const docs: any[] = [];
    if (this._historyList && this._historyList.length > 0) {
      this._historyList.forEach(h => {
        if (h.documents && Array.isArray(h.documents) && h.documents.length > 0) {
          h.documents.forEach((d: any) => {
            const fname = d.fileName || d.documentName || d.name || '';
            if (fname) {
              const cleanFname = fname.split('/').pop() || fname;
              docs.push({
                fileName: cleanFname,
                stageName: d.stageName || h.stageName || '',
                subStatusName: d.subStatusName || h.toSubStatusName || '',
                uploadedBy: d.uploadedBy || h.changedByName || '',
                uploadedAt: d.createdAt || h.changedAt,
                fileUrl: this.getFileUrl(fname),
                isImage: this.isImageDoc(cleanFname),
                isPdf: this.isPdfDoc(cleanFname),
                isWord: this.isWordDoc(cleanFname),
                isExcel: this.isExcelDoc(cleanFname),
                isVideo: this.isVideoDoc(cleanFname),
                isAudio: this.isAudioDoc(cleanFname),
                fileTypeLabel: this.getFileTypeLabel(cleanFname)
              });
            }
          });
        } else if (h.images) {
          const parsedFiles = this.parseHistoryDocuments(h.images);
          parsedFiles.forEach((fname: string) => {
            if (fname && fname.trim()) {
              const cleanFname = fname.trim().split('/').pop() || fname.trim();
              docs.push({
                fileName: cleanFname,
                stageName: h.stageName || '',
                subStatusName: h.toSubStatusName || '',
                uploadedBy: h.changedByName || '',
                uploadedAt: h.changedAt,
                fileUrl: this.getFileUrl(fname.trim()),
                isImage: this.isImageDoc(cleanFname),
                isPdf: this.isPdfDoc(cleanFname),
                isWord: this.isWordDoc(cleanFname),
                isExcel: this.isExcelDoc(cleanFname),
                isVideo: this.isVideoDoc(cleanFname),
                isAudio: this.isAudioDoc(cleanFname),
                fileTypeLabel: this.getFileTypeLabel(cleanFname)
              });
            }
          });
        }
      });
    }
    this._allHistoryDocuments = docs;

    // 2. Form entries
    const entries: any[] = [];
    if (this._historyList && this._historyList.length > 0) {
      this._historyList.forEach(h => {
        if (h.formValues) {
          const fields = this.parseFormValues(h.formValues);
          if (fields.length > 0) {
            entries.push({
              stageName: h.stageName || '',
              subStatusName: h.toSubStatusName || '',
              submittedBy: h.changedByName || '',
              submittedAt: h.changedAt,
              fields: fields
            });
          }
        }
      });
    }
    this._allHistoryFormEntries = entries;
  }

  trackByDoc(index: number, item: any): string {
    return item ? (item.fileUrl || item.fileName || index.toString()) : index.toString();
  }

  trackByHistory(index: number, item: any): string | number {
    return item ? (item.historyId || item.id || index) : index;
  }

  trackByDocName(index: number, item: string): string {
    return item || index.toString();
  }

  trackByEntry(index: number, item: any): string | number {
    return item ? (item.submittedAt || index) : index;
  }

  trackByField(index: number, item: any): string {
    return item ? (item.key || index.toString()) : index.toString();
  }

  downloadDocument(fileUrl: string, fileName?: string): void {
    if (!fileUrl) return;
    const name = fileName || fileUrl.split('fileName=').pop()?.split('/').pop() || 'download';
    this.downloadImageService.downloadImageNew(fileUrl, name);
  }

  isImageDoc(fileName: string): boolean {
    const ext = (fileName || '').split('.').pop()?.toLowerCase() || '';
    return ['jpg', 'jpeg', 'png', 'gif', 'svg', 'webp'].includes(ext);
  }

  isPdfDoc(fileName: string): boolean {
    const ext = (fileName || '').split('.').pop()?.toLowerCase() || '';
    return ext === 'pdf';
  }

  isWordDoc(fileName: string): boolean {
    const ext = (fileName || '').split('.').pop()?.toLowerCase() || '';
    return ['doc', 'docx'].includes(ext);
  }

  isExcelDoc(fileName: string): boolean {
    const ext = (fileName || '').split('.').pop()?.toLowerCase() || '';
    return ['xls', 'xlsx', 'csv'].includes(ext);
  }

  isVideoDoc(fileName: string): boolean {
    const ext = (fileName || '').split('.').pop()?.toLowerCase() || '';
    return ['mp4', 'avi', 'mov', 'wmv', 'mkv'].includes(ext);
  }

  isAudioDoc(fileName: string): boolean {
    const ext = (fileName || '').split('.').pop()?.toLowerCase() || '';
    return ['mp3', 'wav', 'ogg', 'm4a'].includes(ext);
  }

  getFileTypeLabel(fileName: string): string {
    const ext = (fileName || '').split('.').pop()?.toLowerCase() || '';
    if (this.isPdfDoc(fileName)) return 'application/pdf';
    if (this.isWordDoc(fileName)) return 'application/vnd.word';
    if (this.isExcelDoc(fileName)) return 'application/vnd.excel';
    if (this.isImageDoc(fileName)) return `image/${ext}`;
    if (this.isVideoDoc(fileName)) return `video/${ext}`;
    if (this.isAudioDoc(fileName)) return `audio/${ext}`;
    return ext ? `file/${ext}` : 'document';
  }

  getDocIcon(fileName: string): string {
    const ext = (fileName || '').split('.').pop()?.toLowerCase() || '';
    if (['jpg', 'jpeg', 'png', 'gif', 'svg', 'webp'].includes(ext)) return 'image';
    if (['pdf'].includes(ext)) return 'picture_as_pdf';
    if (['doc', 'docx'].includes(ext)) return 'description';
    if (['xls', 'xlsx', 'csv'].includes(ext)) return 'table_chart';
    if (['ppt', 'pptx'].includes(ext)) return 'slideshow';
    return 'insert_drive_file';
  }

  getDocColorClass(fileName: string): string {
    const ext = (fileName || '').split('.').pop()?.toLowerCase() || '';
    if (['pdf'].includes(ext)) return 'doc-red';
    if (['doc', 'docx'].includes(ext)) return 'doc-blue';
    if (['xls', 'xlsx', 'csv'].includes(ext)) return 'doc-green';
    if (['jpg', 'jpeg', 'png', 'gif', 'svg', 'webp'].includes(ext)) return 'doc-purple';
    return 'doc-gray';
  }
}


